import { useState, useEffect, useMemo } from 'react';
import {
  Box, Heading, Text, useToast, useDisclosure,
  VStack, Flex, Spinner, Center, Input, IconButton,
  InputGroup, InputLeftElement,
} from '@chakra-ui/react';
import { AddIcon, SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { fetchCurrencies, updateCurrency, deleteCurrency } from '../../api/endpointsBackend';
import ConfirmationDialog from '../shared/ConfirmationDialog';
import CurrencyDetailPanel from '../currencies/CurrencyDetailPanel';
import CurrencyAvatar from '../currencies/CurrencyAvatar';

import Card from '../shared/Card';
import RateSourceCard from './RateSourceCard';
const PANEL_CREATE_MODE = 'new';
const TAB_KEYS = ['active', 'inactive', 'all'];

const Toggle = ({ isChecked, onChange, label }) => (
  <Box
    as="button"
    type="button"
    role="switch"
    aria-checked={isChecked}
    aria-label={label}
    onClick={onChange}
    position="relative"
    flexShrink={0}
    w="30px"
    h="17px"
    borderRadius="full"
    bg={isChecked ? 'redesign.heading' : 'redesign.text.faint'}
    transition="background-color 0.2s"
    cursor="pointer"
  >
    <Box
      position="absolute"
      top="2px"
      left={isChecked ? '15px' : '2px'}
      w="13px"
      h="13px"
      borderRadius="full"
      bg="redesign.bg.surface"
      boxShadow="0 1px 2px rgba(0,0,0,0.2)"
      transition="left 0.2s"
    />
  </Box>
);

const CurrencyRow = ({ currency, selected, onSelect, onToggle, isLast }) => {
  const { t } = useTranslation('currencies');
  const isInactive = !currency.is_active;
  const isStandard = currency.type === 'standard';
  return (
    <Flex
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
      }}
      position="relative"
      align="center"
      gap={3}
      px={4}
      py={3}
      borderBottom={isLast ? 'none' : '1px solid'}
      borderColor="redesign.border"
      cursor="pointer"
      bg={selected ? 'redesign.bg.accent' : 'transparent'}
      _hover={{ bg: selected ? 'redesign.bg.accent' : 'redesign.bg.hover' }}
      opacity={isInactive ? 0.55 : 1}
      transition="background-color 0.15s"
      outline="none"
      _focusVisible={{ bg: 'redesign.bg.hover' }}
    >
      {selected && (
        <Box
          position="absolute"
          left={0}
          top="8px"
          bottom="8px"
          w="3px"
          bg="redesign.accent.blue"
          borderRightRadius="sm"
        />
      )}
      <CurrencyAvatar name={currency.name} size={36} isInactive={isInactive} />
      <Box flex={1} minW={0}>
        <Text fontSize="sm" fontWeight={600} color="redesign.heading" lineHeight={1.1} noOfLines={1}>
          {currency.name}
        </Text>
        {currency.description && (
          <Text fontSize="xs" color="redesign.text.muted" mt={1} lineHeight={1.1} noOfLines={1}>
            {currency.description}
          </Text>
        )}
      </Box>
      {isStandard && (
        <Text
          textStyle="microLabel"
          bg="redesign.bg.card"
          px={2}
          py="2px"
          borderRadius="sm"
        >
          {t('standard')}
        </Text>
      )}
      <Box onClick={(e) => e.stopPropagation()}>
        <Toggle
          isChecked={currency.is_active}
          onChange={() => onToggle(currency)}
          label={t('toggleAria', { name: currency.name })}
        />
      </Box>
    </Flex>
  );
};

export default function CurrenciesContent() {
  const { t } = useTranslation('currencies');
  const [currencies, setCurrencies] = useState([]);
  const [selectedCurrencyId, setSelectedCurrencyId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState('active');
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [currencyToDelete, setCurrencyToDelete] = useState(null);
  const toast = useToast();
  const isPanelOpen = selectedCurrencyId !== null;

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      setLoading(true);
      setCurrencies(await fetchCurrencies(null) || []);
    } catch {
      toast({ title: t('toasts.errorTitle'), description: t('toasts.loadFailed'), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (currency) => {
    try {
      await updateCurrency(currency.id, { is_active: !currency.is_active });
      await load();
      toast({
        description: currency.is_active
          ? t('toasts.deactivated', { name: currency.name })
          : t('toasts.activated', { name: currency.name }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch {
      toast({ title: t('toasts.errorTitle'), description: t('toasts.updateFailed'), status: 'error', duration: 5000, isClosable: true });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!currencyToDelete) return;
    try {
      setLoading(true);
      await deleteCurrency(currencyToDelete.id);
      toast({ description: t('toasts.deleted'), status: 'success', duration: 3000, isClosable: true });
      onDeleteClose();
      if (selectedCurrencyId === currencyToDelete.id) setSelectedCurrencyId(null);
      setCurrencyToDelete(null);
      await load();
    } catch (err) {
      const msg = err.response?.status === 400
        ? t('toasts.standardCannotDelete')
        : t('toasts.deleteFailed');
      toast({ title: t('toasts.errorTitle'), description: msg, status: 'error', duration: 7000, isClosable: true });
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return currencies
      .filter(c => {
        if (tab === 'active' && !c.is_active) return false;
        if (tab === 'inactive' && c.is_active) return false;
        const s = searchTerm.trim().toLowerCase();
        if (!s) return true;
        return c.name?.toLowerCase().includes(s) || c.description?.toLowerCase().includes(s);
      })
      .sort((a, b) => (a.is_active === b.is_active ? 0 : a.is_active ? -1 : 1));
  }, [currencies, tab, searchTerm]);

  return (
    <Flex gap={0} h="100%" overflow="hidden">
      {/* List */}
      <Box
        flex={1}
        overflowY="auto"
        minW={0}
        display={isPanelOpen ? 'none' : 'block'}
      >
        <Flex justify="space-between" align="flex-end" mb={4} gap={4}>
          <Heading as="h2" size="md" color="redesign.heading">{t('title')}</Heading>
          <Flex align="center" gap={2}>
            <Flex
              role="tablist"
              align="center"
              bg="redesign.bg.sunken"
              border="1px solid"
              borderColor="redesign.border"
              p="2px"
              gap="2px"
              borderRadius="8px"
              fontSize="xs"
              fontWeight={500}
            >
              {TAB_KEYS.map((key) => {
                const isActive = tab === key;
                return (
                  <Box
                    as="button"
                    key={key}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setTab(key)}
                    px={3}
                    py="6px"
                    borderRadius="6px"
                    bg={isActive ? 'redesign.bg.raised' : 'transparent'}
                    color={isActive ? 'redesign.heading' : 'redesign.text.muted'}
                    boxShadow={isActive ? '0 1px 2px rgba(16, 24, 40, 0.10)' : 'none'}
                    _dark={{ boxShadow: 'none' }}
                    _hover={!isActive ? { color: 'redesign.heading' } : {}}
                    transition="all 0.15s"
                    cursor="pointer"
                  >
                    {t(`tabs.${key}`)}
                  </Box>
                );
              })}
            </Flex>
            <IconButton
              icon={<AddIcon boxSize={2.5} />}
              onClick={() => setSelectedCurrencyId(PANEL_CREATE_MODE)}
              aria-label={t('newAria')}
              size="xs"
              variant="ghost"
              color="redesign.text.muted"
              _hover={{ color: 'redesign.heading', bg: 'redesign.bg.card' }}
            />
          </Flex>
        </Flex>

        <RateSourceCard />

        <InputGroup mb={4}>
          <InputLeftElement pointerEvents="none" h="full">
            <SearchIcon color="redesign.text.muted" boxSize={3} />
          </InputLeftElement>
          <Input
            placeholder={t('searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            borderRadius="md"
            fontSize="sm"
          />
        </InputGroup>

        {loading ? (
          <Center py={16}><Spinner size="lg" color="redesign.heading" /></Center>
        ) : filtered.length === 0 ? (
          <Center py={16}>
            <VStack spacing={3}>
              <Text fontSize="sm" fontWeight={600} color="redesign.heading">
                {searchTerm
                  ? t('empty.noMatch', { query: searchTerm.trim() })
                  : tab === 'inactive'
                    ? t('empty.noInactive')
                    : tab === 'active'
                      ? t('empty.noActive')
                      : t('empty.noCurrencies')}
              </Text>
              <Text fontSize="xs" color="redesign.text.muted" textAlign="center" maxW="280px">
                {searchTerm ? t('empty.noMatchHint') : t('empty.noCurrenciesHint')}
              </Text>
            </VStack>
          </Center>
        ) : (
          <Card
            borderRadius="lg"
            overflow="hidden"
          >
            {filtered.map((c, i) => (
              <CurrencyRow
                key={c.id}
                currency={c}
                selected={c.id === selectedCurrencyId}
                onSelect={() => setSelectedCurrencyId(c.id)}
                onToggle={handleToggle}
                isLast={i === filtered.length - 1}
              />
            ))}
          </Card>
        )}
      </Box>

      {/* Detail panel */}
      {isPanelOpen && (
        <Box
          flex={1}
          w="100%"
          overflowY="auto"
        >
          <CurrencyDetailPanel
            currencyId={selectedCurrencyId === PANEL_CREATE_MODE ? null : selectedCurrencyId}
            onClose={() => setSelectedCurrencyId(null)}
            onCurrencyUpdated={load}
            onDelete={(c) => { setCurrencyToDelete(c); onDeleteOpen(); }}
          />
        </Box>
      )}

      <ConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDeleteConfirm}
        title={t('deleteDialog.title')}
        message={currencyToDelete ? t('deleteDialog.message', { name: currencyToDelete.name }) : ''}
        confirmText={t('deleteDialog.confirm')}
        confirmColorScheme="red"
        isLoading={loading}
      />
    </Flex>
  );
}
